<template>
  <div class="w-full">
    <div class="w-[90%] max-w-5xl mx-auto py-14">
      <h1 class="text-4xl font-semibold mb-2">Chatbot Knowledge Base</h1>
      <p class="text-grey text-sm mb-8">Manage what the DODO chatbot knows. Add chunks manually or paste a document for AI extraction.</p>

      <!-- Tabs -->
      <div class="flex gap-1 mb-8 border-b border-neutral-200">
        <button
          v-for="tab in tabs" :key="tab.key"
          @click="activeTab = tab.key"
          class="px-5 py-2.5 text-sm font-medium transition-colors"
          :class="activeTab === tab.key ? 'border-b-2 border-black text-black -mb-px' : 'text-grey hover:text-black'"
        >
          {{ tab.label }}
          <span v-if="tab.key === 'duplicates' && duplicateGroups.length" class="ml-1.5 text-xs bg-red-100 text-red-600 px-1.5 py-0.5 rounded-full">{{ duplicateGroups.length }}</span>
        </button>
      </div>

      <!-- ── TAB: Knowledge Chunks ───────────────────────────────────── -->
      <div v-if="activeTab === 'chunks'">
        <div class="flex justify-between items-center mb-5">
          <span class="text-sm">{{ chunks.length }} chunk{{ chunks.length === 1 ? '' : 's' }}</span>
          <button @click="openAddModal" class="bg-black text-white text-sm px-4 py-2 rounded hover:bg-neutral-800 transition-colors">
            + Add Chunk
          </button>
        </div>

        <div v-if="loadingChunks" class="pt-20 flex justify-center"><spinner /></div>
        <div v-else-if="!chunks.length" class="py-16 text-center text-grey text-sm">No chunks yet. Add one or use the AI Parse tab.</div>

        <div v-else class="space-y-3">
          <div
            v-for="chunk in chunks" :key="chunk.id"
            class="border border-neutral-200 rounded-lg p-4 flex items-start justify-between gap-4"
            :class="!chunk.is_active ? 'opacity-50' : ''"
          >
            <div class="flex-1 min-w-0">
              <div class="flex items-center gap-2 mb-1">
                <span class="font-medium text-sm">{{ chunk.title }}</span>
                <span class="text-xs bg-neutral-100 text-neutral-500 px-2 py-0.5 rounded font-mono">{{ chunk.topic }}</span>
                <span v-if="!chunk.is_active" class="text-xs bg-yellow-100 text-yellow-700 px-2 py-0.5 rounded">Inactive</span>
              </div>
              <p class="text-xs text-grey line-clamp-2 mb-2">{{ chunk.content }}</p>
              <div class="flex flex-wrap gap-1">
                <span v-for="kw in chunk.keywords" :key="kw" class="text-xs bg-green-50 text-qorayGreen px-2 py-0.5 rounded">{{ kw }}</span>
              </div>
            </div>
            <div class="flex gap-2 shrink-0">
              <button @click="openEditModal(chunk)" class="text-xs border border-neutral-200 px-3 py-1.5 rounded hover:bg-neutral-50 transition-colors">Edit</button>
              <button @click="toggleActive(chunk)" class="text-xs border px-3 py-1.5 rounded transition-colors"
                :class="chunk.is_active ? 'border-yellow-300 text-yellow-700 hover:bg-yellow-50' : 'border-green-300 text-qorayGreen hover:bg-green-50'"
              >{{ chunk.is_active ? 'Disable' : 'Enable' }}</button>
              <button @click="confirmDelete(chunk)" class="text-xs border border-red-200 text-red-500 px-3 py-1.5 rounded hover:bg-red-50 transition-colors">Delete</button>
            </div>
          </div>
        </div>
      </div>

      <!-- ── TAB: AI Parse ───────────────────────────────────────────── -->
      <div v-if="activeTab === 'parse'">
        <div v-if="!parsedChunks.length">
          <p class="text-sm text-grey mb-4">Paste the full content of your DODO programme document below. The AI will extract structured knowledge chunks for you to review.</p>
          <textarea
            v-model="docContent"
            placeholder="Paste document content here..."
            rows="12"
            class="w-full border border-neutral-200 rounded-lg p-4 text-sm focus:outline-none focus:border-black resize-none font-mono leading-relaxed"
          ></textarea>
          <button
            @click="parseDocument"
            :disabled="!docContent.trim() || parsing"
            class="mt-4 bg-black text-white text-sm px-6 py-2.5 rounded hover:bg-neutral-800 transition-colors disabled:opacity-40 flex items-center gap-2"
          >
            <span v-if="parsing" class="w-3.5 h-3.5 border-2 border-white border-t-transparent rounded-full animate-spin inline-block"></span>
            {{ parsing ? 'Parsing with AI...' : 'Parse with AI →' }}
          </button>
        </div>

        <div v-else>
          <div class="flex items-center justify-between mb-5">
            <p class="text-sm font-medium">
              {{ newChunks.length }} new
              <span v-if="updateChunks.length"> · {{ updateChunks.length }} enrichment{{ updateChunks.length === 1 ? '' : 's' }} of existing chunks</span>
              — review and save
            </p>
            <button @click="parsedChunks = []; docContent = ''" class="text-sm text-grey underline">← Re-parse</button>
          </div>

          <!-- New chunks -->
          <div v-if="newChunks.length" class="mb-6">
            <p class="text-xs text-grey font-medium uppercase tracking-wide mb-3">New Chunks ({{ newChunks.length }})</p>
            <div class="space-y-4">
              <div v-for="(chunk, i) in parsedChunks.filter(c => c.action !== 'update')" :key="'new-' + i" class="border border-neutral-200 rounded-lg p-4">
                <div class="grid grid-cols-2 gap-3 mb-3">
                  <div>
                    <label class="text-xs text-grey font-medium block mb-1">Title</label>
                    <input v-model="chunk.title" class="w-full border border-neutral-200 rounded px-3 py-1.5 text-sm focus:outline-none focus:border-black" />
                  </div>
                  <div>
                    <label class="text-xs text-grey font-medium block mb-1">Topic slug</label>
                    <input v-model="chunk.topic" class="w-full border border-neutral-200 rounded px-3 py-1.5 text-sm font-mono focus:outline-none focus:border-black" />
                  </div>
                </div>
                <div class="mb-3">
                  <label class="text-xs text-grey font-medium block mb-1">Keywords (comma-separated)</label>
                  <input
                    :value="chunk.keywords.join(', ')"
                    @input="chunk.keywords = $event.target.value.split(',').map(k => k.trim()).filter(Boolean)"
                    class="w-full border border-neutral-200 rounded px-3 py-1.5 text-sm focus:outline-none focus:border-black"
                  />
                </div>
                <div>
                  <label class="text-xs text-grey font-medium block mb-1">Content</label>
                  <textarea v-model="chunk.content" rows="4" class="w-full border border-neutral-200 rounded px-3 py-1.5 text-sm focus:outline-none focus:border-black resize-none leading-relaxed"></textarea>
                </div>
              </div>
            </div>
          </div>

          <!-- Enrichment updates -->
          <div v-if="updateChunks.length" class="mb-6">
            <p class="text-xs text-grey font-medium uppercase tracking-wide mb-3">Enrichments — Updates to Existing Chunks ({{ updateChunks.length }})</p>
            <div class="space-y-4">
              <div v-for="(chunk, i) in parsedChunks.filter(c => c.action === 'update')" :key="'upd-' + i" class="border border-amber-200 bg-amber-50 rounded-lg p-4">
                <div class="flex items-center gap-2 mb-3">
                  <span class="text-xs font-medium bg-amber-100 text-amber-700 px-2 py-0.5 rounded">Updating</span>
                  <span class="text-sm text-amber-800 font-medium">{{ chunk.existingTitle || chunk.topic }}</span>
                </div>
                <div class="grid grid-cols-2 gap-3 mb-3">
                  <div>
                    <label class="text-xs text-grey font-medium block mb-1">Title</label>
                    <input v-model="chunk.title" class="w-full border border-amber-200 rounded px-3 py-1.5 text-sm focus:outline-none focus:border-amber-400 bg-white" />
                  </div>
                  <div>
                    <label class="text-xs text-grey font-medium block mb-1">Topic slug</label>
                    <input v-model="chunk.topic" class="w-full border border-amber-200 rounded px-3 py-1.5 text-sm font-mono focus:outline-none focus:border-amber-400 bg-white" />
                  </div>
                </div>
                <div class="mb-3">
                  <label class="text-xs text-grey font-medium block mb-1">Keywords (comma-separated)</label>
                  <input
                    :value="chunk.keywords.join(', ')"
                    @input="chunk.keywords = $event.target.value.split(',').map(k => k.trim()).filter(Boolean)"
                    class="w-full border border-amber-200 rounded px-3 py-1.5 text-sm focus:outline-none focus:border-amber-400 bg-white"
                  />
                </div>
                <div>
                  <label class="text-xs text-grey font-medium block mb-1">Merged content (existing + new)</label>
                  <textarea v-model="chunk.content" rows="5" class="w-full border border-amber-200 rounded px-3 py-1.5 text-sm focus:outline-none focus:border-amber-400 resize-none leading-relaxed bg-white"></textarea>
                </div>
              </div>
            </div>
          </div>

          <button
            @click="bulkSave"
            :disabled="saving"
            class="bg-black text-white text-sm px-6 py-2.5 rounded hover:bg-neutral-800 transition-colors disabled:opacity-40 flex items-center gap-2"
          >
            <span v-if="saving" class="w-3.5 h-3.5 border-2 border-white border-t-transparent rounded-full animate-spin inline-block"></span>
            {{ saving ? 'Saving...' : `Save All ${parsedChunks.length} (${newChunks.length} new · ${updateChunks.length} updated)` }}
          </button>
        </div>
      </div>

      <!-- ── TAB: Unanswered Questions ───────────────────────────────── -->
      <div v-if="activeTab === 'unanswered'">
        <div v-if="loadingUnanswered" class="pt-20 flex justify-center"><spinner /></div>
        <div v-else-if="!unanswered.length" class="py-16 text-center text-grey text-sm">No unanswered questions yet.</div>
        <div v-else class="space-y-2">
          <div
            v-for="q in unanswered" :key="q.id"
            class="border border-neutral-200 rounded-lg px-4 py-3 flex items-start justify-between gap-4"
          >
            <p class="text-sm text-neutral-800">{{ q.question }}</p>
            <span class="text-xs text-grey shrink-0">{{ $store.getters.formatDate(q.asked_at) }}</span>
          </div>
        </div>
      </div>

      <!-- ── TAB: Duplicates ─────────────────────────────────────────── -->
      <div v-if="activeTab === 'duplicates'">
        <div class="flex items-center justify-between mb-5">
          <p class="text-sm text-grey">AI scans all active chunks and groups ones that overlap or repeat.</p>
          <button
            @click="detectDuplicates"
            :disabled="detecting"
            class="bg-black text-white text-sm px-5 py-2.5 rounded hover:bg-neutral-800 disabled:opacity-40 flex items-center gap-2"
          >
            <span v-if="detecting" class="w-3.5 h-3.5 border-2 border-white border-t-transparent rounded-full animate-spin inline-block"></span>
            {{ detecting ? 'Scanning...' : '🔍 Detect Duplicates' }}
          </button>
        </div>

        <div v-if="!duplicateGroups.length && !detecting" class="py-16 text-center text-grey text-sm">
          {{ dupScanDone ? 'No duplicates found. Knowledge base looks clean!' : 'Click "Detect Duplicates" to scan.' }}
        </div>

        <div v-if="duplicateGroups.length" class="space-y-6 mb-6">
          <div
            v-for="(group, gi) in duplicateGroups" :key="gi"
            class="border border-neutral-200 rounded-xl overflow-hidden"
            :class="group.dismissed ? 'opacity-40' : ''"
          >
            <!-- Group header -->
            <div class="bg-neutral-50 px-5 py-3 flex items-center justify-between border-b border-neutral-200">
              <span class="text-sm font-medium">Group {{ gi + 1 }} — {{ group.ids.length }} chunks to merge</span>
              <button @click="group.dismissed = !group.dismissed" class="text-xs text-grey underline">
                {{ group.dismissed ? 'Restore' : 'Skip' }}
              </button>
            </div>

            <div v-if="!group.dismissed" class="p-5">
              <!-- Original chunks being replaced -->
              <p class="text-xs text-grey font-medium mb-2 uppercase tracking-wide">Chunks being merged</p>
              <div class="space-y-1 mb-5">
                <div v-for="summary in group.chunkSummaries" :key="summary.id" class="flex items-center gap-2 text-sm">
                  <span :class="summary.id === group.keepId ? 'text-qorayGreen font-medium' : 'text-neutral-400 line-through'">
                    {{ summary.title }}
                  </span>
                  <span class="text-xs text-neutral-400 font-mono">{{ summary.topic }}</span>
                  <span v-if="summary.id === group.keepId" class="text-xs bg-green-50 text-qorayGreen px-1.5 py-0.5 rounded">base</span>
                </div>
              </div>

              <!-- Merged result (editable) -->
              <p class="text-xs text-grey font-medium mb-2 uppercase tracking-wide">Merged result (editable)</p>
              <div class="space-y-3">
                <div class="grid grid-cols-2 gap-3">
                  <div>
                    <label class="text-xs text-grey block mb-1">Title</label>
                    <input v-model="group.mergedTitle" class="w-full border border-neutral-200 rounded px-3 py-1.5 text-sm focus:outline-none focus:border-black" />
                  </div>
                  <div>
                    <label class="text-xs text-grey block mb-1">Topic slug</label>
                    <input v-model="group.mergedTopic" class="w-full border border-neutral-200 rounded px-3 py-1.5 text-sm font-mono focus:outline-none focus:border-black" />
                  </div>
                </div>
                <div>
                  <label class="text-xs text-grey block mb-1">Keywords</label>
                  <input
                    :value="group.mergedKeywords.join(', ')"
                    @input="group.mergedKeywords = $event.target.value.split(',').map(k => k.trim()).filter(Boolean)"
                    class="w-full border border-neutral-200 rounded px-3 py-1.5 text-sm focus:outline-none focus:border-black"
                  />
                </div>
                <div>
                  <label class="text-xs text-grey block mb-1">Content</label>
                  <textarea v-model="group.mergedContent" rows="5" class="w-full border border-neutral-200 rounded px-3 py-1.5 text-sm focus:outline-none focus:border-black resize-none leading-relaxed"></textarea>
                </div>
              </div>
            </div>
          </div>
        </div>

        <button
          v-if="activeMergeGroups.length"
          @click="applyMerges"
          :disabled="applyingMerges"
          class="bg-black text-white text-sm px-6 py-2.5 rounded hover:bg-neutral-800 disabled:opacity-40 flex items-center gap-2"
        >
          <span v-if="applyingMerges" class="w-3.5 h-3.5 border-2 border-white border-t-transparent rounded-full animate-spin inline-block"></span>
          {{ applyingMerges ? 'Applying...' : `Apply ${activeMergeGroups.length} Merge${activeMergeGroups.length === 1 ? '' : 's'}` }}
        </button>
      </div>
    </div>

    <!-- ── Add / Edit Modal ──────────────────────────────────────────── -->
    <div v-if="modal.open" class="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
      <div class="bg-white rounded-xl w-full max-w-lg p-6">
        <h2 class="text-xl font-semibold mb-5">{{ modal.mode === 'add' ? 'Add Chunk' : 'Edit Chunk' }}</h2>
        <div class="space-y-4">
          <div class="grid grid-cols-2 gap-3">
            <div>
              <label class="text-xs text-grey font-medium block mb-1">Title *</label>
              <input v-model="modal.form.title" class="w-full border border-neutral-200 rounded px-3 py-2 text-sm focus:outline-none focus:border-black" />
            </div>
            <div>
              <label class="text-xs text-grey font-medium block mb-1">Topic slug *</label>
              <input v-model="modal.form.topic" class="w-full border border-neutral-200 rounded px-3 py-2 text-sm font-mono focus:outline-none focus:border-black" placeholder="e.g. capital_requirements" />
            </div>
          </div>
          <div>
            <label class="text-xs text-grey font-medium block mb-1">Keywords (comma-separated) *</label>
            <input
              :value="modal.form.keywords.join(', ')"
              @input="modal.form.keywords = $event.target.value.split(',').map(k => k.trim()).filter(Boolean)"
              class="w-full border border-neutral-200 rounded px-3 py-2 text-sm focus:outline-none focus:border-black"
              placeholder="capital, invest, ₦, fund"
            />
          </div>
          <div>
            <label class="text-xs text-grey font-medium block mb-1">Content *</label>
            <textarea v-model="modal.form.content" rows="6" class="w-full border border-neutral-200 rounded px-3 py-2 text-sm focus:outline-none focus:border-black resize-none leading-relaxed"></textarea>
          </div>
        </div>
        <div class="flex justify-end gap-3 mt-6">
          <button @click="modal.open = false" class="text-sm border border-neutral-200 px-4 py-2 rounded hover:bg-neutral-50">Cancel</button>
          <button @click="saveChunk" :disabled="modal.saving" class="bg-black text-white text-sm px-5 py-2 rounded hover:bg-neutral-800 disabled:opacity-40 flex items-center gap-2">
            <span v-if="modal.saving" class="w-3.5 h-3.5 border-2 border-white border-t-transparent rounded-full animate-spin inline-block"></span>
            {{ modal.saving ? 'Saving...' : 'Save' }}
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import axios from "@/axios.js";
import Spinner from "@/components/Spinner.vue";

export default {
  components: { Spinner },
  data() {
    return {
      activeTab: "chunks",
      tabs: [
        { key: "chunks", label: "Knowledge Chunks" },
        { key: "parse", label: "AI Parse" },
        { key: "duplicates", label: "Duplicates" },
        { key: "unanswered", label: "Unanswered Questions" },
      ],
      chunks: [],
      loadingChunks: true,
      unanswered: [],
      loadingUnanswered: false,
      docContent: "",
      parsedChunks: [],
      parsing: false,
      saving: false,
      duplicateGroups: [],
      detecting: false,
      applyingMerges: false,
      dupScanDone: false,
      modal: {
        open: false,
        mode: "add",
        editId: null,
        saving: false,
        form: { title: "", topic: "", content: "", keywords: [] },
      },
    };
  },
  watch: {
    activeTab(val) {
      if (val === "unanswered" && !this.unanswered.length) this.fetchUnanswered();
    },
  },
  computed: {
    activeMergeGroups() {
      return this.duplicateGroups.filter(g => !g.dismissed);
    },
    newChunks() {
      return this.parsedChunks.filter(c => c.action !== 'update');
    },
    updateChunks() {
      return this.parsedChunks.filter(c => c.action === 'update');
    },
  },
  mounted() {
    this.fetchChunks();
  },
  methods: {
    authHeaders() {
      return { headers: { Authorization: `Bearer ${this.$store.getters.token}` } };
    },
    async detectDuplicates() {
      this.detecting = true;
      this.dupScanDone = false;
      this.duplicateGroups = [];
      try {
        const { data } = await axios.post(
          "/admin/api/chatbot-knowledge/detect-duplicates",
          { bot: 'dodo' },
          this.authHeaders()
        );
        this.duplicateGroups = (data.data || []).map(g => ({ ...g, dismissed: false }));
        this.dupScanDone = true;
      } catch (err) {
        this.$toast.error('Error detecting duplicates: ' + (err.response?.data?.error || err.message));
      } finally {
        this.detecting = false;
      }
    },
    async applyMerges() {
      if (!this.activeMergeGroups.length) return;
      this.applyingMerges = true;
      try {
        const merges = this.activeMergeGroups.map(g => ({
          keepId: g.keepId,
          deleteIds: g.ids.filter(id => id !== g.keepId),
          mergedTitle: g.mergedTitle,
          mergedTopic: g.mergedTopic,
          mergedContent: g.mergedContent,
          mergedKeywords: g.mergedKeywords,
        }));
        const { data } = await axios.post(
          "/admin/api/chatbot-knowledge/apply-merges",
          { merges },
          this.authHeaders()
        );
        this.$toast.success(data.message);
        this.duplicateGroups = [];
        this.dupScanDone = false;
        await this.fetchChunks();
        this.activeTab = 'chunks';
      } catch (err) {
        this.$toast.error('Error applying merges: ' + (err.response?.data?.error || err.message));
      } finally {
        this.applyingMerges = false;
      }
    },
    async fetchChunks() {
      this.loadingChunks = true;
      try {
        const { data } = await axios.get("/admin/api/chatbot-knowledge?bot=dodo", this.authHeaders());
        this.chunks = data.data;
      } catch (e) {
        this.$toast.error('Failed to load knowledge chunks.');
      } finally {
        this.loadingChunks = false;
      }
    },
    async fetchUnanswered() {
      this.loadingUnanswered = true;
      try {
        const { data } = await axios.get("/admin/api/chatbot-knowledge/unanswered?bot=dodo", this.authHeaders());
        this.unanswered = data.data;
      } catch (e) {
        this.$toast.error('Failed to load unanswered questions.');
      } finally {
        this.loadingUnanswered = false;
      }
    },
    openAddModal() {
      this.modal = { open: true, mode: "add", editId: null, saving: false, form: { title: "", topic: "", content: "", keywords: [] } };
    },
    openEditModal(chunk) {
      this.modal = {
        open: true, mode: "edit", editId: chunk.id, saving: false,
        form: { title: chunk.title, topic: chunk.topic, content: chunk.content, keywords: [...(chunk.keywords || [])] },
      };
    },
    async saveChunk() {
      const { title, topic, content, keywords } = this.modal.form;
      if (!title || !topic || !content) return this.$toast.warn('Title, topic, and content are required.');
      this.modal.saving = true;
      try {
        if (this.modal.mode === "add") {
          await axios.post("/admin/api/chatbot-knowledge", { bot: "dodo", title, topic, content, keywords }, this.authHeaders());
        } else {
          await axios.patch(`/admin/api/chatbot-knowledge/${this.modal.editId}`, { title, topic, content, keywords }, this.authHeaders());
        }
        this.modal.open = false;
        await this.fetchChunks();
      } catch (e) {
        this.$toast.error('Failed to save chunk.');
      } finally {
        this.modal.saving = false;
      }
    },
    async toggleActive(chunk) {
      try {
        await axios.patch(`/admin/api/chatbot-knowledge/${chunk.id}`, { is_active: !chunk.is_active }, this.authHeaders());
        await this.fetchChunks();
      } catch (e) {
        this.$toast.error('Failed to update chunk.');
      }
    },
    async confirmDelete(chunk) {
      if (!confirm(`Delete "${chunk.title}"? This cannot be undone.`)) return;
      try {
        await axios.delete(`/admin/api/chatbot-knowledge/${chunk.id}`, this.authHeaders());
        await this.fetchChunks();
      } catch (e) {
        this.$toast.error('Failed to delete chunk.');
      }
    },
    async parseDocument() {
      this.parsing = true;
      try {
        const { data } = await axios.post("/admin/api/chatbot-knowledge/parse", { content: this.docContent, bot: "dodo" }, this.authHeaders());
        this.parsedChunks = data.data;
      } catch (e) {
        this.$toast.error('Failed to parse document. Try again.');
      } finally {
        this.parsing = false;
      }
    },
    async bulkSave() {
      this.saving = true;
      try {
        const { data } = await axios.post("/admin/api/chatbot-knowledge/bulk-save", { chunks: this.parsedChunks }, this.authHeaders());
        this.$toast.success(data.message || 'Saved successfully.');
        this.parsedChunks = [];
        this.docContent = "";
        this.activeTab = "chunks";
        await this.fetchChunks();
      } catch (e) {
        this.$toast.error('Failed to save chunks.');
      } finally {
        this.saving = false;
      }
    },
  },
};
</script>
